import React, { useCallback } from 'react';
import { Box, Typography, IconButton, Paper, Tooltip } from '@mui/material';
import { 
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { MessageProps } from '../../../types/chat';
import MessageBubble from './MessageBubble';

const FileMessage: React.FC<MessageProps> = React.memo(({ 
  message, 
  isCurrentUser,
  onImagePress,
  onReactionPress,
  onReplyPress,
  onDeletePress,
  currentUserId,
  currentUserType
}) => {
  const handleReactionPress = (emoji: string) => {
    onReactionPress?.(message, emoji);
  };

  // Helper function to format file size
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }, []);

  const handleDownload = useCallback(async () => {
    const fileUrl = message.pdfUrl || message.fileUrl;
    if (!fileUrl) return;
    
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Determine file name and extension
      const fileName = message.metadata?.originalFileName || 
        message.fileName || 
        (message.messageType === 'pdf' ? `document-${message.id}.pdf` : `file-${message.id}`);
      
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  }, [message.pdfUrl, message.fileUrl, message.fileName, message.id, message.messageType]);

  const handlePdfView = useCallback(() => {
    const fileUrl = message.pdfUrl || message.fileUrl;
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  }, [message.pdfUrl, message.fileUrl]);

  const fileUrl = message.pdfUrl || message.fileUrl;
  const isPdf = message.messageType === 'pdf' || message.pdfUrl;
  
  // Use metadata first, then fallback to message properties
  const fileName = message.metadata?.originalFileName || 
    message.fileName || 
    (isPdf ? 'Document.pdf' : 'File');
  
  // Format file size from metadata (bytes) or use string from message
  const fileSize = message.metadata?.fileSize 
    ? formatFileSize(message.metadata.fileSize)
    : message.fileSize;

  if (!fileUrl) {
    return (
      <MessageBubble 
        message={message} 
        isCurrentUser={isCurrentUser}
        onReactionPress={handleReactionPress}
        onReplyPress={onReplyPress}
        currentUserId={currentUserId}
      >
        <Box sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          File not available
        </Box>
      </MessageBubble>
    );
  }

  return (
    <MessageBubble 
      message={message} 
      isCurrentUser={isCurrentUser}
      onReactionPress={handleReactionPress}
      onReplyPress={onReplyPress}
      onDeletePress={onDeletePress}
      onImagePress={onImagePress}
      currentUserId={currentUserId}
      currentUserType={currentUserType}
    >
      <Tooltip 
        title={isPdf ? "Click to view PDF in new tab" : "Click to download file"}
        arrow
      >
        <Paper
          elevation={1}
          sx={{
            p: 2,
            maxWidth: 300,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            cursor: 'pointer',
            '&:hover': {
              elevation: 2,
              backgroundColor: 'action.hover',
            },
            border: '1px solid',
            borderColor: 'divider',
          }}
          onClick={isPdf ? handlePdfView : handleDownload}
        >
        {/* File icon */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minWidth: 40,
          height: 40,
          borderRadius: 1,
          backgroundColor: isPdf ? 'error.light' : 'primary.light',
          color: 'white'
        }}>
          {isPdf ? <PdfIcon /> : <FileIcon />}
        </Box>

        {/* File info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {fileName}
          </Typography>
          {fileSize && (
            <Typography variant="caption" color="text.secondary">
              {fileSize}
            </Typography>
          )}
        </Box>

        {/* Download button */}
        <Tooltip title="Download file" arrow>
          <IconButton 
            size="small" 
            sx={{ 
              backgroundColor: 'action.selected',
              '&:hover': {
                backgroundColor: 'action.focus',
              }
            }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering the parent's onClick
              handleDownload();
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
      </Tooltip>

      {/* Optional message text with file */}
      {message.message && message.message.trim() && (
        <Box sx={{ mt: 1, fontSize: '14px', lineHeight: 1.4 }}>
          {message.message}
        </Box>
      )}
    </MessageBubble>
  );
});

FileMessage.displayName = 'FileMessage';

export default FileMessage;
